import clsx from 'clsx';
import type { ReactNode } from 'react';
import { createElement, forwardRef } from 'react';

import type { MergeElementProps } from '../../../utils/react.util';
import styles from './Button.module.css';

export interface ButtonRenderProps {
  type?: 'submit' | 'reset' | 'button';
  className?: string;
  id?: string;
  onClick?: React.MouseEventHandler;
}

export type ButtonProps<
  T extends React.ElementType<ButtonRenderProps> = 'button',
> = MergeElementProps<
  T,
  {
    /**
     * Override the root element type.
     *
     * @default button
     */
    Component?: T;

    /**
     * Display the button as a block level element that fills the available
     * horizontal space.
     *
     * @default false
     */
    fullWidth?: boolean;

    /**
     * Button label content (alias for `children`)
     */
    label?: ReactNode;

    /**
     * Button kind: 'primary' or 'secondary' (defaults to 'primary')
     *
     * @default primary
     */
    kind?: 'primary' | 'secondary';
  }
>;

/**
 * Primary button component
 */
export const Button = forwardRef(
  <T extends React.ElementType<ButtonRenderProps> = 'button'>(
    props: ButtonProps<T>,
    ref: React.ForwardedRef<React.ElementRef<T>>,
  ) => {
    const {
      id,
      Component = 'button',
      fullWidth,
      className,
      label,
      kind = 'primary',
      ...baseProps
    } = props;

    const renderProps: ButtonRenderProps = {
      ref,
      id,
      className: clsx(
        className,
        styles.root,
        kind === 'secondary' ? styles.secondary : styles.primary,
        ' focus:outline-none',
        {
          [styles.fullWidth]: fullWidth,
        },
      ),
      type: 'button',
      children: label,
      ...baseProps,
    };

    return createElement(Component, renderProps);
  },
);
